import { ActionReducerMapBuilder, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { microsoftConsumerSecurityClient } from 'client-factories/MicrosoftConsumerSecurityClientFactoryV1';
import { isDeviceAlertWebViewEnabled, isGroupItmBreachesEnabled } from 'common/utilities/FlightSetting';
import { localeIgnoreCaseEquals } from 'common/utilities/String';
import {
  Alert,
  AlertsGetAlertsOptionalParams,
  AlertsGetAlertsSummaryOptionalParams,
  PaginationMetadata,
  SettingDetails,
  ThreatDetails,
  UnsafeUrlDetails,
} from 'mcsclient/models';
import { ApiCallResultStatus } from 'store/apiCallResultStatus';
import { createAsyncApiThunk, createOneOffAsyncApiThunk } from 'store/reduxHelpers';

import { IAlertDetails, IPaginationData } from './alerts.types';
import { alertHasHistory, isDeviceSettingEventType, isDeviceThreatEventType } from './alertsHelpers';
import { getAllAlertsForArtifactIdAndType } from './alertsSelectors';
import { getDefaultAlertsState, IAlertsState } from './alertsState';

const initialState: IAlertsState = getDefaultAlertsState();

const pageSize = 20;
const normalizeKey = (key: string) => key.toLowerCase();

const getDefaultAlertDetails = (
  alertId: string
): Pick<IAlertDetails, 'id' | 'fetchAlertApiStatus' | 'fetchAlertError' | 'updateAlertsApiStatus' | 'updateAlertsError'> => ({
  id: normalizeKey(alertId),
  fetchAlertApiStatus: ApiCallResultStatus.DEFAULT,
  fetchAlertError: undefined,
  updateAlertsApiStatus: ApiCallResultStatus.DEFAULT,
  updateAlertsError: undefined,
});

const buildPaginationData = (loadedItemsCount: number, paginationMetadata: PaginationMetadata | undefined): IPaginationData => {
  const { totalActiveItemCount = 0, totalItemCount = 0, activeItemCount = 0 } = paginationMetadata || {};
  return {
    loadedItemsCount,
    pendingItemsCount: Math.max(0, totalItemCount - loadedItemsCount),
    totalItemCount,
    totalActiveItemCount,
    activeItemCount,
    totalInactiveItemCount: totalItemCount - totalActiveItemCount,
    loadedPages: Math.ceil(loadedItemsCount / pageSize),
  };
};

const fetchAlerts = (count: number, pageNumber: number, fetchSummary = false) => {
  const options: AlertsGetAlertsSummaryOptionalParams & AlertsGetAlertsOptionalParams = {
    includeFamily: true,
    pageSize: count,
    pageNumber,
  };
  if (!isDeviceAlertWebViewEnabled()) {
    // support only itp alerts until xDevice detail view is ready
    options.alertTypes = ['Itp'];
  }

  if (isGroupItmBreachesEnabled()) {
    options.itmBreachesGrouping = true;
  }

  return fetchSummary
    ? microsoftConsumerSecurityClient.alerts.getAlertsSummary(options)
    : microsoftConsumerSecurityClient.alerts.getAlerts(options);
};

export async function refreshAlertCountsPostAction<T>(func: () => Promise<T>, refreshAlertCounts = false) {
  const response = await func();
  let allAlertsPaginationMetadata: PaginationMetadata | undefined;
  let dashboardAlertsPaginationMetadata: PaginationMetadata | undefined;
  if (refreshAlertCounts) {
    const [allAlerts, dashboardAlerts] = await Promise.all([fetchAlerts(1, 1), fetchAlerts(1, 1, true)]);
    allAlertsPaginationMetadata = allAlerts.paginationMetadata;
    dashboardAlertsPaginationMetadata = dashboardAlerts.paginationMetadata;
  }

  return { response, allAlertsPaginationMetadata, dashboardAlertsPaginationMetadata };
}

function setPaginationMetadata(
  state: IAlertsState,
  allAlertsPaginationMetadata: PaginationMetadata | undefined,
  dashboardAlertsPaginationMetadata: PaginationMetadata | undefined
) {
  if (allAlertsPaginationMetadata) {
    state.allAlerts.paginationData = buildPaginationData(state.allAlerts.alertIds.length, allAlertsPaginationMetadata);
  }
  if (dashboardAlertsPaginationMetadata) {
    state.dashboardAlerts.paginationData = buildPaginationData(
      state.dashboardAlerts.alertIds.length,
      dashboardAlertsPaginationMetadata
    );
  }
}

function removeInactiveDashboardAlertsByIds(state: IAlertsState, alertIds: string[]) {
  if (alertIds && alertIds.length > 0) {
    const nonDashboardAlertIds = alertIds.map((id) => normalizeKey(id));

    if (nonDashboardAlertIds.length > 0) {
      state.dashboardAlerts.alertIds = state.dashboardAlerts.alertIds.filter(
        (id) => nonDashboardAlertIds.indexOf(normalizeKey(id)) === -1
      );
    }
  }
}

function removeInactiveAlertsFromDashboardAlerts(state: IAlertsState, alerts: Alert[] | undefined) {
  if (alerts && alerts.length > 0) {
    const alertIds = alerts.filter((a) => !a.isVisibleOnDashboard).map((a) => a.id);
    removeInactiveDashboardAlertsByIds(state, alertIds);
  }
}

export const getUpdatedAlert = (
  alertDetailsMap: Record<string, IAlertDetails | undefined>,
  alertId: string,
  newAlert: Alert,
  partial?: Partial<IAlertDetails>
) =>
  ({
    ...getDefaultAlertDetails(alertId), // default alert details
    ...alertDetailsMap[alertId], // existing alert details
    ...newAlert, // new alert details
    ...partial, // any additional patch to the alert details
  }) as IAlertDetails;

export const getAllAlertsPageAsync = createOneOffAsyncApiThunk(
  'alerts/getAllAlertsPageAsync',
  (pageNumber: number) => fetchAlerts(pageSize, pageNumber),
  ({ alerts }) => alerts.allAlerts.fetchPageApiStatus
);

const handleGetAllAlertsPageAsync = (builder: ActionReducerMapBuilder<IAlertsState>): void => {
  builder
    .addCase(getAllAlertsPageAsync.pending, (state) => {
      state.allAlerts.fetchPageApiStatus = ApiCallResultStatus.LOADING;
    })
    .addCase(getAllAlertsPageAsync.fulfilled, (state, { payload }) => {
      const { alerts, paginationMetadata } = payload;
      let newAlertIds = state.allAlerts.alertIds.concat((alerts || []).map((a) => normalizeKey(a.id)));

      // de-dupe
      newAlertIds = Array.from(new Set(newAlertIds));

      state.allAlerts.alertIds = newAlertIds;
      if (alerts) {
        alerts.forEach((alert) => {
          const alertId = normalizeKey(alert.id);
          state.alertDetailsMap[alertId] = getUpdatedAlert(state.alertDetailsMap, alertId, alert, {
            fetchAlertApiStatus: ApiCallResultStatus.SUCCESS,
          });
        });
      }

      const { totalItemCount = 0 } = paginationMetadata || {};
      state.allAlerts.paginationData = buildPaginationData(newAlertIds.length, paginationMetadata);

      if (newAlertIds.length >= totalItemCount || (alerts?.length ?? 0) === 0) {
        // if this was the last page
        state.allAlerts.fetchPageApiStatus = ApiCallResultStatus.SUCCESS;
      } else {
        // more data left
        state.allAlerts.fetchPageApiStatus = ApiCallResultStatus.DEFAULT;
      }
    })
    .addCase(getAllAlertsPageAsync.rejected, (state, { error }) => {
      state.allAlerts.fetchPageApiStatus = ApiCallResultStatus.FAILED;
      state.allAlerts.fetchPageError = error.message;
    });
};

export const getDashboardAlertsPageAsync = createOneOffAsyncApiThunk(
  'alerts/getDashboardAlertsPageAsync',
  (pageNumber: number) => fetchAlerts(pageSize, pageNumber, true),
  ({ alerts }) => alerts.dashboardAlerts.fetchPageApiStatus
);

const handleGetDashboardAlertsPageAsync = (builder: ActionReducerMapBuilder<IAlertsState>): void => {
  builder
    .addCase(getDashboardAlertsPageAsync.pending, (state) => {
      state.dashboardAlerts.fetchPageApiStatus = ApiCallResultStatus.LOADING;
    })
    .addCase(getDashboardAlertsPageAsync.fulfilled, (state, { payload }) => {
      const { alerts, paginationMetadata } = payload;
      let newAlertIds = state.dashboardAlerts.alertIds.concat((alerts || []).map((a) => normalizeKey(a.id)));

      // de-dupe
      newAlertIds = Array.from(new Set(newAlertIds));

      state.dashboardAlerts.alertIds = newAlertIds;
      if (alerts) {
        alerts.forEach((alert) => {
          const alertId = normalizeKey(alert.id);
          state.alertDetailsMap[alertId] = getUpdatedAlert(state.alertDetailsMap, alertId, alert, {
            dashboardData: {
              title: alert.title,
              subtitle: alert.subtitle,
            },
            fetchAlertApiStatus: ApiCallResultStatus.SUCCESS,
          });
        });
      }

      const { totalItemCount = 0 } = paginationMetadata || {};
      state.dashboardAlerts.paginationData = buildPaginationData(newAlertIds.length, paginationMetadata);

      if (newAlertIds.length >= totalItemCount || (alerts?.length ?? 0) === 0) {
        // if this was the last page
        state.dashboardAlerts.fetchPageApiStatus = ApiCallResultStatus.SUCCESS;
      } else {
        // more data left
        state.dashboardAlerts.fetchPageApiStatus = ApiCallResultStatus.DEFAULT;
      }
    })
    .addCase(getDashboardAlertsPageAsync.rejected, (state, { error }) => {
      state.dashboardAlerts.fetchPageApiStatus = ApiCallResultStatus.FAILED;
      state.dashboardAlerts.fetchPageError = error.message;
    });
};

export const getAlertDetailsAsync = createOneOffAsyncApiThunk(
  'alerts/getAlertDetailsAsync',
  ({ alertId, refreshAlertCounts = false }: { alertId: string; refreshAlertCounts?: boolean }) =>
    refreshAlertCountsPostAction(async () => {
      let deviceAlertDetails: UnsafeUrlDetails | SettingDetails | ThreatDetails;
      const alert = await microsoftConsumerSecurityClient.alerts.getAlert(alertId, { includeFamily: true });
      if (isDeviceAlertWebViewEnabled() && alertHasHistory(alert)) {
        if (isDeviceSettingEventType(alert)) {
          deviceAlertDetails = await microsoftConsumerSecurityClient.alerts.getSettingAlertDetails(alertId, { includeFamily: true });
        } else if (isDeviceThreatEventType(alert)) {
          deviceAlertDetails = await microsoftConsumerSecurityClient.alerts.getThreatAlertDetails(alertId, { includeFamily: true });
        } else {
          deviceAlertDetails = await microsoftConsumerSecurityClient.alerts.getUnsafeUrlAlertDetails(alertId, { includeFamily: true });
        }
        return { ...alert, deviceAlertDetails };
      }
      return alert;
    }, refreshAlertCounts),
  ({ alerts }, { alertId }) => alerts.alertDetailsMap[normalizeKey(alertId)]?.fetchAlertApiStatus || ApiCallResultStatus.DEFAULT
);

const handleGetAlertDetailsAsync = (builder: ActionReducerMapBuilder<IAlertsState>): void => {
  builder
    .addCase(getAlertDetailsAsync.pending, (state, { meta }) => {
      const alertId = normalizeKey(meta.arg.alertId);
      if (!state.alertDetailsMap[alertId]) {
        state.alertDetailsMap[alertId] = {
          ...getDefaultAlertDetails(alertId),
          fetchAlertApiStatus: ApiCallResultStatus.LOADING,
        } as IAlertDetails;
      } else {
        state.alertDetailsMap[alertId].fetchAlertApiStatus = ApiCallResultStatus.LOADING;
      }
    })
    .addCase(getAlertDetailsAsync.fulfilled, (state, { payload, meta }) => {
      const alertId = normalizeKey(meta.arg.alertId);
      const { response: alert, allAlertsPaginationMetadata, dashboardAlertsPaginationMetadata } = payload;
      state.alertDetailsMap[alertId] = getUpdatedAlert(state.alertDetailsMap, alertId, alert, {
        fetchAlertApiStatus: ApiCallResultStatus.SUCCESS,
      });
      removeInactiveAlertsFromDashboardAlerts(state, [alert]);
      setPaginationMetadata(state, allAlertsPaginationMetadata, dashboardAlertsPaginationMetadata);
    })
    .addCase(getAlertDetailsAsync.rejected, (state, { error, meta }) => {
      const alertId = normalizeKey(meta.arg.alertId);
      state.alertDetailsMap[alertId]!.fetchAlertApiStatus = ApiCallResultStatus.FAILED;
      state.alertDetailsMap[alertId]!.fetchAlertError = error.message;
    });
};

export const getAlertsForBreachesAsync = createAsyncApiThunk(
  'alerts/getAlertsForBreachesAsync',
  ({ breachIds, refreshAlertCounts = false }: { breachIds: string[]; refreshAlertCounts?: boolean; csid?: string }) =>
    refreshAlertCountsPostAction(
      async () =>
        (
          await microsoftConsumerSecurityClient.alerts.getAlerts({
            breachIds,
            includeFamily: true,
          })
        ).alerts,
      refreshAlertCounts
    )
);

const handleGetAlertsForBreachesAsync = (builder: ActionReducerMapBuilder<IAlertsState>): void => {
  builder
    .addCase(getAlertsForBreachesAsync.pending, (state, { meta }) => {
      const { breachIds, csid } = meta.arg;
      const breachAlerts = getAllAlertsForArtifactIdAndType(state, breachIds, 'IdentityMonitoringBreach', csid);

      breachAlerts.forEach((alert) => {
        const alertId = normalizeKey(alert.id);
        state.alertDetailsMap[alertId] = getUpdatedAlert(state.alertDetailsMap, alertId, alert, {
          fetchAlertApiStatus: ApiCallResultStatus.LOADING,
        });
      });
    })
    .addCase(getAlertsForBreachesAsync.fulfilled, (state, { payload }) => {
      const { response: alerts, allAlertsPaginationMetadata, dashboardAlertsPaginationMetadata } = payload;

      if (alerts) {
        alerts.forEach((alert) => {
          const alertId = normalizeKey(alert.id);
          state.alertDetailsMap[alertId] = getUpdatedAlert(state.alertDetailsMap, alertId, alert, {
            fetchAlertApiStatus: ApiCallResultStatus.SUCCESS,
          });
        });

        removeInactiveAlertsFromDashboardAlerts(state, alerts);
        setPaginationMetadata(state, allAlertsPaginationMetadata, dashboardAlertsPaginationMetadata);
      }
    })
    .addCase(getAlertsForBreachesAsync.rejected, (state, { meta }) => {
      const { breachIds, csid } = meta.arg;
      const breachAlerts = getAllAlertsForArtifactIdAndType(state, breachIds, 'IdentityMonitoringBreach', csid);

      breachAlerts.forEach((alert) => {
        const alertId = normalizeKey(alert.id);
        state.alertDetailsMap[alertId] = getUpdatedAlert(state.alertDetailsMap, alertId, alert, {
          fetchAlertApiStatus: ApiCallResultStatus.FAILED,
        });
      });
    });
};

export const dismissAlertAsync = createOneOffAsyncApiThunk(
  'alerts/dismissAlertAsync',
  ({ alertId, refreshAlertCounts = false }: { alertId: string; refreshAlertCounts?: boolean }) =>
    refreshAlertCountsPostAction(
      () => microsoftConsumerSecurityClient.alerts.updateAlertMethod(alertId, { body: { isDismissed: true } }),
      refreshAlertCounts
    ),
  ({ alerts }, { alertId }) =>
    alerts.alertDetailsMap[normalizeKey(alertId)]?.updateAlertsApiStatus !== ApiCallResultStatus.LOADING
      ? ApiCallResultStatus.DEFAULT
      : ApiCallResultStatus.LOADING
);

const handleDismissAlertAsync = (builder: ActionReducerMapBuilder<IAlertsState>): void => {
  builder
    .addCase(dismissAlertAsync.pending, (state, { meta }) => {
      const alertId = normalizeKey(meta.arg.alertId);

      if (!state.alertDetailsMap[alertId]) {
        state.alertDetailsMap[alertId] = {
          ...getDefaultAlertDetails(alertId),
          fetchAlertApiStatus: ApiCallResultStatus.LOADING,
          updateAlertsApiStatus: ApiCallResultStatus.LOADING,
        } as IAlertDetails;
      } else {
        state.alertDetailsMap[alertId].updateAlertsApiStatus = ApiCallResultStatus.LOADING;
      }
    })
    .addCase(dismissAlertAsync.fulfilled, (state, { payload, meta }) => {
      const alertId = normalizeKey(meta.arg.alertId);
      const { response: alert, allAlertsPaginationMetadata, dashboardAlertsPaginationMetadata } = payload;

      if (!alert.id) {
        // Handle an empty alert object
        // This can happen if a group alert was dismissed and API returned 204 meaning
        // the group alert was not found as all linked alerts are in resolved state
        delete state.alertDetailsMap[alertId];
      } else {
        state.alertDetailsMap[alertId] = getUpdatedAlert(state.alertDetailsMap, alertId, alert, {
          fetchAlertApiStatus: ApiCallResultStatus.SUCCESS,
          updateAlertsApiStatus: ApiCallResultStatus.DEFAULT,
        });
      }

      // use alertId instead of alert object as alert could be empty for group alert dismissal
      removeInactiveDashboardAlertsByIds(state, [alertId]);
      setPaginationMetadata(state, allAlertsPaginationMetadata, dashboardAlertsPaginationMetadata);
    })
    .addCase(dismissAlertAsync.rejected, (state, { error, meta }) => {
      const alertId = normalizeKey(meta.arg.alertId);
      state.alertDetailsMap[alertId]!.fetchAlertApiStatus = ApiCallResultStatus.DEFAULT;
      state.alertDetailsMap[alertId]!.updateAlertsApiStatus = ApiCallResultStatus.FAILED;
      state.alertDetailsMap[alertId]!.updateAlertsError = error.message;
    });
};

export const alertsSlice = createSlice({
  name: 'alerts',
  initialState,
  reducers: {
    removeAlert: (state, action: PayloadAction<string>) => {
      const alertId = normalizeKey(action.payload);
      if (state.alertDetailsMap[alertId]) {
        delete state.alertDetailsMap[alertId];
      }
      state.allAlerts.alertIds = state.allAlerts.alertIds.filter((id) => !localeIgnoreCaseEquals(id, alertId));
      if (state.allAlerts.paginationData) {
        state.allAlerts.paginationData.totalItemCount = Math.max(state.allAlerts.paginationData.totalItemCount - 1, 0);
        state.allAlerts.paginationData.totalActiveItemCount = Math.max(state.allAlerts.paginationData.totalActiveItemCount - 1, 0);
        state.allAlerts.paginationData.activeItemCount = Math.max(state.allAlerts.paginationData.activeItemCount - 1, 0);
        state.allAlerts.paginationData.loadedItemsCount = Math.max(state.allAlerts.paginationData.loadedItemsCount - 1, 0);
      }
      if (state.dashboardAlerts.alertIds.find((id) => localeIgnoreCaseEquals(id, alertId))) {
        state.dashboardAlerts.alertIds = state.dashboardAlerts.alertIds.filter((id) => !localeIgnoreCaseEquals(id, alertId));
        if (state.dashboardAlerts.paginationData) {
          state.dashboardAlerts.paginationData.totalItemCount = Math.max(state.dashboardAlerts.paginationData.totalItemCount - 1, 0);
          state.dashboardAlerts.paginationData.totalActiveItemCount = Math.max(
            state.dashboardAlerts.paginationData.totalActiveItemCount - 1,
            0
          );
          state.dashboardAlerts.paginationData.activeItemCount = Math.max(state.dashboardAlerts.paginationData.activeItemCount - 1, 0);
          state.dashboardAlerts.paginationData.loadedItemsCount = Math.max(
            state.dashboardAlerts.paginationData.loadedItemsCount - 1,
            0
          );
        }
      }
    },
    resetAlerts: () => getDefaultAlertsState(),
  },
  extraReducers: (builder) => {
    handleGetAllAlertsPageAsync(builder);
    handleGetDashboardAlertsPageAsync(builder);
    handleGetAlertDetailsAsync(builder);
    handleGetAlertsForBreachesAsync(builder);
    handleDismissAlertAsync(builder);
  },
});

export const { removeAlert, resetAlerts } = alertsSlice.actions;

export default alertsSlice.reducer;
